import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import { OwnerFinanceDialog } from "./center/OwnerQuickDialogs";

const ACCOUNT_TYPES = [
  ["bank", "Banco"], ["wallet", "Billetera"], ["cash", "Efectivo"],
  ["investment", "Inversión"], ["receivable", "Por cobrar"], ["debt", "Deuda"], ["other", "Otra"],
];


function money(value, currency) {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function inventoryUnitLabel(value) {
  const labels = { unit: "unidades", pack: "paquetes", g: "g", kg: "kg", ml: "ml", l: "litros", portion: "porciones", other: "u." };
  return labels[value] || value || "unidades";
}


export default function OwnerFinancePage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = React.useState([]);
  const [transactions, setTransactions] = React.useState([]);
  const [inventoryItems, setInventoryItems] = React.useState([]);
  const [recurring, setRecurring] = React.useState(null);
  const [movementDialogOpen, setMovementDialogOpen] = React.useState(false);
  const [movementInitialType, setMovementInitialType] = React.useState("expense");
  const [transactionToRecurring, setTransactionToRecurring] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState({ name: "", type: "bank", currency: "ARS", openingBalance: "" });

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [accountData, transactionData, recurringData, inventoryData] = await Promise.all([
        ownerApi.listFinanceAccounts(),
        ownerApi.listFinanceTransactions({ limit: 80 }),
        ownerApi.listRecurringFinance(),
        ownerApi.listInventoryItems({ limit: 80 }),
      ]);
      setAccounts(accountData.accounts || []);
      setTransactions(transactionData.transactions || []);
      setRecurring(recurringData.recurring || null);
      setInventoryItems(inventoryData.items || []);
    } catch (err) {
      setError(formatApiError(err, "No se pudieron cargar tus finanzas"));
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const createAccount = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await ownerApi.createFinanceAccount({ ...accountForm, openingBalance: Number(accountForm.openingBalance || 0) });
      setAccountForm((current) => ({ ...current, name: "", openingBalance: "" }));
      await load();
    } catch (err) {
      setError(formatApiError(err, "No se pudo crear la cuenta"));
    } finally {
      setSaving(false);
    }
  };


  return (
    <Box sx={{ pb: 1 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: .65 }}><Button size="small" variant="outlined" onClick={() => navigate("/dashboard/owner/movimientos")} sx={{ minHeight: 32, textTransform: "none", fontWeight: 900 }}>Movimientos</Button></Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(320px,.8fr) minmax(0,1.6fr)" }, gap: 2, mb: 2 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent component="form" onSubmit={createAccount}>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Nueva cuenta</Typography>
            <Stack spacing={1.3}>
              <TextField label="Nombre" value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} required size="small" />
              <TextField select label="Tipo" value={accountForm.type} onChange={(e) => setAccountForm({ ...accountForm, type: e.target.value })} size="small">
                {ACCOUNT_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <TextField label="Moneda" value={accountForm.currency} onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value.toUpperCase() })} size="small" />
                <TextField label="Saldo inicial" type="number" value={accountForm.openingBalance} onChange={(e) => setAccountForm({ ...accountForm, openingBalance: e.target.value })} size="small" />
              </Box>
              <Button type="submit" variant="contained" startIcon={<AddRoundedIcon />} disabled={saving} sx={{ textTransform: "none", fontWeight: 950 }}>
                Crear cuenta
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Mi plata</Typography>
            {!accounts.length ? <Typography sx={{ color: "text.secondary" }}>Creá tu primera cuenta para empezar.</Typography> : null}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
              {accounts.map((account) => {
                const accountId = account.id || account._id;
                return (
                  <Box
                    key={accountId}
                    component="button"
                    type="button"
                    onClick={() => navigate(`/dashboard/owner/movimientos?cuenta=${encodeURIComponent(accountId)}`)}
                    sx={{
                      p: 1.5, borderRadius: 2.3, border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.10)",
                      bgcolor: "transparent", color: "inherit", textAlign: "left", cursor: "pointer", font: "inherit",
                      transition: "border-color .15s ease, background-color .15s ease",
                      "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
                      "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 950 }}>{account.name}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 850 }}>{account.type} · {account.currency}</Typography>
                    <Typography variant="h6" sx={{ mt: .6, fontWeight: 1000 }}>{money(account.balance, account.currency)}</Typography>
                    <Typography sx={{ mt: .35, color: "primary.main", fontSize: ".72rem", fontWeight: 900 }}>Ver por qué tengo este saldo →</Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,.9fr) minmax(0,1.1fr)" }, gap: 2, mb: 2 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>Registrar movimiento</Typography>
            <Typography sx={{ color: "text.secondary", mt: .5, mb: 1.5, fontSize: ".86rem" }}>
              Registrá la fecha real, reutilizá categorías anteriores y decidí si una compra suma stock. El consumo baja stock sin tocar tu plata.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!accounts.length} onClick={() => { setMovementInitialType("expense"); setMovementDialogOpen(true); }} sx={{ textTransform: "none", fontWeight: 950 }}>
                Gasto, ingreso o transferencia
              </Button>
              <Button variant="outlined" disabled={!inventoryItems.length} onClick={() => { setMovementInitialType("consumption"); setMovementDialogOpen(true); }} sx={{ textTransform: "none", fontWeight: 900 }}>
                Consumir stock
              </Button>
            </Stack>
            {!accounts.length ? <Typography sx={{ color: "text.secondary", mt: 1, fontSize: ".78rem" }}>Creá una cuenta antes de registrar movimientos de dinero.</Typography> : null}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>Stock personal</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".8rem" }}>Comida, hogar y otros consumibles que ya vinculaste a compras.</Typography>
              </Box>
              <Typography sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>{inventoryItems.length} items</Typography>
            </Stack>
            <Divider sx={{ my: 1.2 }} />
            {!inventoryItems.length ? <Typography sx={{ color: "text.secondary" }}>Todavía no hay stock. En un gasto elegí “Sí, sumar al stock”.</Typography> : null}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: .8 }}>
              {inventoryItems.slice(0, 12).map((item) => (
                <Box key={item._id || item.id} sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900 }} noWrap>{item.name}</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: ".74rem" }}>{Number(item.quantity || 0).toLocaleString("es-AR")} {inventoryUnitLabel(item.unit)}{item.category ? ` · ${item.category}` : ""}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>Últimos movimientos</Typography>
          <Divider sx={{ my: 1.2 }} />
          {!transactions.length ? <Typography sx={{ color: "text.secondary" }}>Todavía no registraste movimientos.</Typography> : null}
          <Stack divider={<Divider flexItem />} spacing={0}>
            {transactions.map((transaction) => {
              const account = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.accountId));
              const from = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.fromAccountId));
              const to = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.toAccountId));
              const sign = transaction.type === "income" ? "+" : transaction.type === "expense" ? "-" : "";
              return (
                <Box key={transaction.id || transaction._id} sx={{ py: 1.25, display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{transaction.description || transaction.category || transaction.type}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {transaction.type === "transfer" ? `${from?.name || "Origen"} → ${to?.name || "Destino"}` : account?.name || "Cuenta"}
                      {transaction.area ? ` · ${transaction.area}` : ""}
                      {transaction.inventory?.itemName ? ` · +${Number(transaction.inventory.quantity || 0)} ${inventoryUnitLabel(transaction.inventory.unit)} ${transaction.inventory.itemName} a stock` : ""}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={.6} alignItems="center">
                    <Typography sx={{ whiteSpace: "nowrap", fontWeight: 1000 }}>{sign}{money(transaction.amount, transaction.currency)}</Typography>
                    {["expense", "income"].includes(transaction.type) ? (
                      transaction.recurringPlanId || (transaction.source?.kind === "recurring" && transaction.source?.entityId)
                        ? <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>Recurrente</Typography>
                        : <Button size="small" startIcon={<RepeatRoundedIcon />} onClick={() => setTransactionToRecurring(transaction)} sx={{ textTransform: "none", fontWeight: 900, minWidth: 0 }}>Recurrente</Button>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      <OwnerFinanceDialog
        open={movementDialogOpen}
        onClose={() => setMovementDialogOpen(false)}
        accounts={accounts}
        initialType={movementInitialType}
        onSaved={load}
      />
      <OwnerRecurringFromTransactionDialog
        open={Boolean(transactionToRecurring)}
        onClose={() => setTransactionToRecurring(null)}
        transaction={transactionToRecurring}
        recurring={recurring}
        onSaved={async () => { setTransactionToRecurring(null); await load(); }}
      />
    </Box>
  );
}
